import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const BorrowOrWaitScenario = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedAnswers, setSelectedAnswers] = useState({})

  // Auto-scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStep])

  const handleAnswer = (stepNum, answer) => {
    setSelectedAnswers({ ...selectedAnswers, [stepNum]: answer })
  }

  // Calculate decision quality score
  const getDecisionQuality = () => {
    const correctAnswers = {
      4: 'B', // What to think about before deciding
      5: 'C', // Understanding interest calculation
      6: 'C', // Installment logic - total vs monthly
      7: 'B', // Notebook final decision
      8: 'C', // Family car loan - comparison
      9: 'B', // Monthly affordability vs total cost
      10: 'C'  // Long-term consequences
    }
    
    let goodDecisions = 0
    Object.keys(correctAnswers).forEach(step => {
      if (selectedAnswers[step] === correctAnswers[step]) {
        goodDecisions++
      }
    })
    
    return goodDecisions
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-purple-100 mb-1">Interaktívny scenár</div>
                <h1 className="text-3xl font-bold">💳 Požičať si alebo počkať?</h1>
              </div>
              {currentStep === 1 ? (
                <button
                  onClick={() => navigate('/scenarios')}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť na ročníky
                </button>
              ) : (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress indicator - STICKY */}
      <div className="bg-white border-b border-gray-200 py-3 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2">
              {[...Array(11)].map((_, idx) => (
                <div
                  key={idx + 1}
                  className={`flex-1 h-2 rounded-full transition-all ${
                    idx + 1 <= currentStep ? 'bg-purple-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">
              Krok {currentStep} z 11
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">

          {/* SLIDE 1: Intro - What this scenario is about */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">💡</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">O čo pôjde v tomto príbehu?</h2>
              </div>

              <div className="max-w-2xl mx-auto">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Niekedy chceme alebo potrebujeme niečo ešte predtým, ako máme dostatok peňazí. V takej situácii musíme rozhodnúť:
                </p>
                
                <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500 mb-6">
                  <p className="text-gray-800 font-medium mb-2">
                    💳 <strong>Požičať si?</strong>
                  </p>
                  <p className="text-gray-700 text-sm">
                    Získam vec hneď, ale zaplatím viac a budem dlho splácať.
                  </p>
                </div>

                <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500 mb-6">
                  <p className="text-gray-800 font-medium mb-2">
                    💰 <strong>Alebo počkať a šetriť?</strong>
                  </p>
                  <p className="text-gray-700 text-sm">
                    Potrvá to dlhšie, ale zaplatím menej a nebudem mať dlhy.
                  </p>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  V tomto scenári sa naučíš, na čo si dávať pozor pri rozhodovaní o pôžičke a ako rozpoznať, kedy sa oplatí počkať.
                </p>
              </div>

              <div className="text-center mt-8">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-8 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition"
                >
                  Pokračovať →
                </button>
              </div>
            </div>
          )}

          {/* SLIDE 2: Basic concepts */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">📚</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Čo by si mal vedieť ako prvé</h2>
                <p className="text-lg font-semibold text-purple-600">Základné pojmy:</p>
              </div>

              <div className="space-y-5 max-w-2xl mx-auto">
                {/* Úver */}
                <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500">
                  <p className="font-bold text-gray-900 mb-3 text-base">💰 Úver</p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Čo to je:</strong> Úver sú peniaze požičané od banky alebo inej inštitúcie, ktoré musíš neskôr vrátiť s úrokmi.
                  </p>
                  <div className="bg-white/60 p-3 rounded">
                    <p className="text-xs font-semibold text-purple-800 mb-2">📌 Príklad:</p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Požičiaš si <strong>100 €</strong></li>
                      <li>• Vrátiš <strong>110 €</strong></li>
                      <li>• Rozdiel (10 €) je <strong>úrok</strong> – cena za požičanie peňazí</li>
                    </ul>
                  </div>
                </div>

                {/* Splátka */}
                <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
                  <p className="font-bold text-gray-900 mb-3 text-base">📅 Splátka</p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Čo to je:</strong> Splátka je pravidelná suma peňazí, ktorou postupne vracias dlh.
                  </p>
                  <div className="bg-white/60 p-3 rounded">
                    <p className="text-xs font-semibold text-blue-800 mb-2">📌 Príklad:</p>
                    <p className="text-sm text-gray-700">
                      Človek platí <strong>časť dlhu každý mesiac</strong>, až kým nie je všetko splatené.
                    </p>
                  </div>
                </div>

                {/* Celková cena pôžičky */}
                <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
                  <p className="font-bold text-gray-900 mb-3 text-base">💸 Celková cena pôžičky</p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Čo to je:</strong> Celková suma, ktorú zaplatíš späť. Zahŕňa požičanú sumu (istinu), úroky a možné poplatky.
                  </p>
                  <div className="bg-white/60 p-3 rounded">
                    <p className="text-xs font-semibold text-green-800 mb-2">📌 Príklad:</p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Požičiaš si <strong>100 €</strong></li>
                      <li>• Vrátiš celkovo <strong>120 €</strong></li>
                      <li>• Celková cena pôžičky je <strong>120 €</strong></li>
                    </ul>
                  </div>
                </div>

                {/* Istina */}
                <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-500">
                  <p className="font-bold text-gray-900 mb-3 text-base">🎯 Istina</p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Čo to je:</strong> Pôvodná požičaná suma bez úrokov a poplatkov.
                  </p>
                  <div className="bg-white/60 p-3 rounded">
                    <p className="text-xs font-semibold text-orange-800 mb-2">📌 Príklad:</p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Požičiaš si <strong>100 €</strong> → toto je <strong>istina</strong></li>
                      <li>• Vrátiš celkovo <strong>120 €</strong></li>
                      <li>• <strong>100 €</strong> = istina</li>
                      <li>• <strong>20 €</strong> = úroky a poplatky</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="text-center mt-8">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-8 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition"
                >
                  Pokračovať →
                </button>
              </div>
            </div>
          )}

          {/* SLIDE 3: Borrow vs Save explanation */}
          {currentStep === 3 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">🤔</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Požičať si alebo šetriť?</h2>
              </div>

              <div className="max-w-2xl mx-auto space-y-6">
                <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500">
                  <p className="font-bold text-gray-900 mb-3">💳 Ak si požičiaš:</p>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">✓</span>
                      <span>Získaš vec <strong>hneď</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600">✗</span>
                      <span>Zaplatíš <strong>viac</strong> (kvôli úrokom)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600">✗</span>
                      <span>Musíš <strong>postupne splácať</strong></span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
                  <p className="font-bold text-gray-900 mb-3">💰 Ak budeš šetriť:</p>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-red-600">✗</span>
                      <span>Získaš vec <strong>neskôr</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600">✓</span>
                      <span>Zaplatíš <strong>menej</strong> (len skutočnú cenu)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600">✓</span>
                      <span><strong>Žiadne dlhy</strong> ani splátky</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-500">
                  <p className="font-bold text-gray-900 mb-3">🎯 Pred rozhodnutím sa opýtaj:</p>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>Koľko peňazí potrebujem?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>Ako dlho by mi trvalo si to našetriť?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>Je to naozaj naliehavá potreba alebo len želanie?</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="text-center mt-8">
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-8 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition"
                >
                  Ukáž mi príklad →
                </button>
              </div>
            </div>
          )}

          {/* SLIDE 4: Marek's situation - NEW NUMBERS */}
          {currentStep === 4 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">💻</div>
                  <h2 className="text-2xl font-bold text-gray-900">Marekov príbeh</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Marek je starší brat, ktorému sa pokazil notebook. Potrebuje nový na školský projekt, ktorý musí odovzdať o 3 týždne.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Notebook, ktorý potrebuje, stojí <strong>1 500 €</strong>.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Marek má práve teraz <strong>100 €</strong> našetrených.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Jeho mesačný príjem je <strong>800 €</strong>, ale po všetkých nevyhnutných výdavkoch mu zostáva len približne <strong>100 € mesačne</strong>, ktoré by mohol šetriť.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Na čo by mal Marek myslieť pred rozhodnutím?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(4, 'A')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Či má v peňaženke aspoň nejaké peniaze hneď teraz</div>
                  <div className="text-sm text-gray-600">Stačí, keď má aspoň niečo</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'B')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-3 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Koľko by celkovo zaplatil pri pôžičke, ako dlho by šetril, a či je to naozaj potrebné hneď</div>
                  <div className="text-sm text-gray-600">Pozrieť sa na celkovú cenu, čas a naliehavosť</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'C')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Či má aspoň nejakých kamarátov, ktorí mu môžu požičať</div>
                  <div className="text-sm text-gray-600">Kamaráti môžu pomôcť</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'D')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Či notebook vyzerá dobre a či sa môže ním chváliť</div>
                  <div className="text-sm text-gray-600">Dôležité je, ako to vypadá</div>
                </button>
              </div>

              {selectedAnswers[4] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[4] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[4] === 'B' ? '✅ Správne!' : '⚠️ Zamysli sa...'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[4] === 'A' ? 'To, že má teraz 100 €, nestačí. Notebook stojí 1 500 €. Musí zistiť, koľko by platil celkovo pri pôžičke a ako dlho by mu trvalo si to našetriť.' :
                      selectedAnswers[4] === 'B' ? 'Presne tak! Marek by mal zvážiť: 1) Koľko by celkovo zaplatil s úrokmi? 2) Ako dlho by šetril (1 500 € - 100 € = 1 400 € / 100 € mesačne = 14 mesiacov)? 3) Je to naozaj naliehavá potreba?' :
                      selectedAnswers[4] === 'C' ? 'Požičiavať si od kamarátov nie je bezpečné riešenie – môže to pokaziť priateľstvá. Lepšie je pozrieť sa na skutočné možnosti: pôžičku alebo šetrenie.' :
                      'Ako notebook vyzerá nie je dôležité. Dôležité je, koľko Marek celkovo zaplatí, ako dlho by šetril a či to naozaj potrebuje hneď.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[4] === 'A' ? 'Marek má 100 €, ale notebook stojí 1 500 €. To nestačí. Musí zvážiť, koľko celkovo zaplatí pri pôžičke (istina + úroky) a či sa mu to oplatí.' :
                      selectedAnswers[4] === 'B' ? 'Pred rozhodnutím o pôžičke je dôležité zvážiť: 1) Celkovú cenu s úrokmi, 2) Ako dlho by trvalo šetrenie (tu 14 mesiacov), 3) Či je to skutočná potreba. Toto sú základné otázky múdreho finančného rozhodovania.' :
                      selectedAnswers[4] === 'C' ? 'Požičiavať si od kamarátov je riskantné – ak nemôže vrátiť, môže to pokaziť priateľstvo. Lepšie je zvážiť pôžičku od banky alebo šetrenie.' :
                      'Vzhľad nie je rozhodujúci faktor pri pôžičke. Dôležité je zvážiť celkovú cenu (istina + úroky), čas potrebný na šetrenie a či je to naozaj naliehavá potreba.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SLIDE 5: Understanding interest calculation */}
          {currentStep === 5 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🧮</div>
                  <h2 className="text-2xl font-bold text-gray-900">Ako fungujú úroky?</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Marek našiel ponuku: <strong>Notebook za 1 500 € na splátky</strong>.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Podmienky: <strong>10 % úrok ročne, spláca 12 mesiacov</strong>.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Celková suma, ktorú zaplatí, bude: <strong>1 500 € (istina) + 150 € (úroky) = 1 650 €</strong>.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Koľko eur zaplatí navyše a koľko % z požičanej sumy to predstavuje?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(5, 'A')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) 100 € navyše, čo je približne 7 %</div>
                  <div className="text-sm text-gray-600">Nižší úrok</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'B')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) 200 € navyše, čo je približne 13 %</div>
                  <div className="text-sm text-gray-600">Vyšší úrok</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'C')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-3 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) 150 € navyše, čo je 10 % z pôvodnej sumy</div>
                  <div className="text-sm text-gray-600">Presný výpočet</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'D')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Žiadne peniaze navyše, lebo splátky sú zadarmo</div>
                  <div className="text-sm text-gray-600">Bez poplatkov</div>
                </button>
              </div>

              {selectedAnswers[5] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[5] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[5] === 'C' ? '✅ Výborne!' : '⚠️ Nie celkom...'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[5] === 'A' ? '100 € by bolo len približne 6,7 %. Úrok je však 10 %, takže navyše zaplatí viac.' :
                      selectedAnswers[5] === 'B' ? '200 € by bolo príliš veľa (13 %). Úrok je 10 %, takže z 1 500 € to je 150 €.' :
                      selectedAnswers[5] === 'C' ? 'Presne tak! 10 % z 1 500 € = 150 €. Marek teda zaplatí navyše 150 € len za to, že si požičal.' :
                      'Splátky nie sú zadarmo. Pri úvere vždy platíš úroky – v tomto prípade 10 %, čo je 150 € navyše.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[5] === 'A' ? 'Úrok 10 % z 1 500 € = 150 €, nie 100 €. To znamená, že celková cena bude 1 650 €.' :
                      selectedAnswers[5] === 'B' ? 'Úrok 10 % z 1 500 € = 150 €, nie 200 €. Celková cena bude 1 650 €.' :
                      selectedAnswers[5] === 'C' ? 'Správny výpočet: 10 % z 1 500 € = 150 €. Celková cena pôžičky bude teda 1 500 € + 150 € = 1 650 €. To je dôležité vedieť pred rozhodnutím.' :
                      'Pri úvere vždy platíš úroky. V tomto prípade 10 % z 1 500 € = 150 € navyše. Celková cena pôžičky je teda 1 650 €.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SLIDE 6: Installment logic - total vs monthly */}
          {currentStep === 6 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">📊</div>
                  <h2 className="text-2xl font-bold text-gray-900">Mesačná splátka vs. celková cena</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Marek porovnáva dve ponuky:
                  </p>
                  <div className="space-y-3">
                    <div className="bg-white p-4 rounded border-l-4 border-blue-400">
                      <p className="font-semibold text-gray-900">Ponuka A:</p>
                      <p className="text-gray-700 text-sm">Mesačná splátka: <strong>120 €</strong></p>
                      <p className="text-gray-700 text-sm">Počet mesiacov: <strong>12</strong></p>
                      <p className="text-gray-700 text-sm">Celková suma: <strong>1 440 €</strong></p>
                    </div>
                    <div className="bg-white p-4 rounded border-l-4 border-purple-400">
                      <p className="font-semibold text-gray-900">Ponuka B:</p>
                      <p className="text-gray-700 text-sm">Mesačná splátka: <strong>150 €</strong></p>
                      <p className="text-gray-700 text-sm">Počet mesiacov: <strong>10</strong></p>
                      <p className="text-gray-700 text-sm">Celková suma: <strong>1 500 €</strong></p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Ktorá ponuka je lepšia a prečo?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(6, 'A')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Ponuka A, lebo nižšia mesačná splátka je vždy lepšia</div>
                  <div className="text-sm text-gray-600">Nižšia splátka = lepšia ponuka</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'B')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Obe sú rovnaké, lebo ide o ten istý notebook</div>
                  <div className="text-sm text-gray-600">Nezáleží na tom</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'C')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-3 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Ponuka B, lebo celková suma je nižšia (ušetrí 60 €), aj keď mesačná splátka je vyššia</div>
                  <div className="text-sm text-gray-600">Pozrieť sa na celkovú cenu</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'D')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Ponuka A, lebo dlhšie splácanie je pohodlnejšie</div>
                  <div className="text-sm text-gray-600">Viac času na splácanie</div>
                </button>
              </div>

              {selectedAnswers[6] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[6] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[6] === 'C' ? '✅ Správne!' : '💭 Premysli si to...'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[6] === 'A' ? 'Nižšia mesačná splátka vyzerá príjemne, ale celková suma je o 60 € vyššia (1 440 € vs. 1 500 €). Nakoniec zaplatíš viac!' :
                      selectedAnswers[6] === 'B' ? 'Ponuky nie sú rovnaké. Aj keď kupuješ ten istý notebook, celková cena sa líši kvôli rôznym úrokom a dĺžke splácania.' :
                      selectedAnswers[6] === 'C' ? 'Presne tak! Ponuka B je celkovo lacnejšia o 60 €. Aj keď mesačná splátka je o 30 € vyššia, celkovo ušetríš.' :
                      'Dlhšie splácanie neznamená lepšiu ponuku. Platíš viac celkovo (1 440 € vs. 1 500 €). Kratšie splácanie s vyššou splátkou je finančne múdrejšie, ak si to môžeš dovoliť.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[6] === 'A' ? 'Ponuka A: 120 € × 12 mesiacov = 1 440 €. Ponuka B: 150 € × 10 mesiacov = 1 500 €. Hoci mesačná splátka je nižšia, celkovo zaplatíš o 60 € menej pri Ponuke B.' :
                      selectedAnswers[6] === 'B' ? 'Aj keď ide o ten istý notebook, ponuky sa líšia. Ponuka A: 1 440 € celkom. Ponuka B: 1 500 € celkom. Rozdiel 60 € môže byť dôležitý.' :
                      selectedAnswers[6] === 'C' ? 'Ponuka B: 150 € × 10 = 1 500 € celkom. Ponuka A: 120 € × 12 = 1 440 €. Hoci mesačná splátka je vyššia, celkovo ušetríš 60 €. Ak si môžeš dovoliť vyššiu splátku, je to múdrejšia voľba.' :
                      'Dlhšie splácanie neznamená lepšiu ponuku. Ponuka A: 1 440 € celkom. Ponuka B: 1 500 € celkom. Kratšie splácanie s vyššou splátkou ti ušetrí peniaze, ak si to môžeš dovoliť.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SLIDE 7: Notebook final decision */}
          {currentStep === 7 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🎯</div>
                  <h2 className="text-2xl font-bold text-gray-900">Marekovo finálne rozhodnutie</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Marek si to zrátał: <em>"Notebook potrebujem na projekt, ktorý musím odovzdať o 3 týždne. Mám 100 €, potrebujem 1 500 €."</em>
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    <em>"Keby som šetril 100 € mesačne, trvalo by mi to <strong>14 mesiacov</strong>. To je príliš dlho – projekt musím odovzdať o 3 týždne!"</em>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <em>"Pri pôžičke by som zaplatil celkovo 1 650 €, čo je o 150 € viac. Ale notebook mám hneď a projekt zvládnem."</em>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Aké je najlepšie rozhodnutie pre Mareka?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(7, 'A')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Počkať 14 mesiacov, ušetriť 150 € a potom kúpiť notebook</div>
                  <div className="text-sm text-gray-600">Šetrenie je vždy lepšie</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'B')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-3 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Požičať si, lebo ide o skutočnú potrebu (projekt o 3 týždne) a 14 mesiacov šetrenia nie je reálne</div>
                  <div className="text-sm text-gray-600">Naliehavá potreba ospravedlňuje pôžičku</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'C')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Kúpiť lacnejší notebook hneď, aj keď nie je taký dobrý</div>
                  <div className="text-sm text-gray-600">Lacnejšia verzia</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'D')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Neodovzdať projekt a počkať na šetrenie</div>
                  <div className="text-sm text-gray-600">Obetovať školu kvôli úsporám</div>
                </button>
              </div>

              {selectedAnswers[7] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[7] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[7] === 'B' ? '✅ Dobrá voľba!' : '💭 Zamysli sa...'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[7] === 'A' ? 'Počkať 14 mesiacov by znamenalo, že Marek nestihne projekt. Tu ide o skutočnú potrebu, nie len želanie. Pôžička má v tomto prípade zmysel.' :
                      selectedAnswers[7] === 'B' ? 'Presne tak! Toto je prípad, kedy pôžička má zmysel. Ide o skutočnú potrebu (projekt), čas je naliehavý (3 týždne) a šetrenie nie je reálne (14 mesiacov). Zaplatí viac, ale získa to, čo potrebuje.' :
                      selectedAnswers[7] === 'C' ? 'Lacnejší notebook môže byť riešenie, ale ak nebude stačiť na projekt, Marek bude mať problém. Pri skutočnej potrebe je lepšie investovať do kvalitného riešenia.' :
                      'Neodovzdať projekt kvôli úsporám nie je múdre. Tu ide o skutočnú potrebu a pôžička má zmysel. Múdre je rozlíšiť, kedy je pôžička opodstatnená a kedy nie.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[7] === 'A' ? 'Šetrenie je zvyčajne múdrejšie, ale tu ide o skutočnú potrebu s naliehavým termínom. Čakať 14 mesiacov nie je reálne. Toto je príklad, kedy pôžička má zmysel – zaplatí 150 € navyše, ale stihne projekt.' :
                      selectedAnswers[7] === 'B' ? 'Toto je príklad múdreho rozhodovania. Marek rozlíšil potrebu od želania. Notebook potrebuje na projekt (3 týždne), nie len chce. Šetrenie by trvalo 14 mesiacov, čo nie je reálne. Zaplatí 150 € navyše, ale získa to, čo potrebuje.' :
                      selectedAnswers[7] === 'C' ? 'Kúpiť lacnejší notebook môže byť riešenie, ale ak nebude spĺňať požiadavky projektu, Marek bude mať problém. Pri skutočnej potrebe je lepšie investovať do kvalitného riešenia, aj keď to znamená pôžičku.' :
                      'Neodovzdať projekt by Marekovi ublížilo v škole. Tu ide o skutočnú potrebu, nie želanie. Pôžička má v tomto prípade zmysel – zaplatí 150 € navyše, ale stihne projekt a nesklame v škole.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SLIDE 8: Family car loan situation */}
          {currentStep === 8 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🚗</div>
                  <h2 className="text-2xl font-bold text-gray-900">Rodičia potrebujú auto</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Rodičia potrebujú nové auto, lebo staré sa pokazilo a nedá sa opraviť. Auto potrebujú do práce.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Auto stojí <strong>12 000 €</strong>. Majú našetrených <strong>2 000 €</strong>, takže potrebujú požičať <strong>10 000 €</strong>.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Porovnávajú dve banky:
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="bg-white p-4 rounded border-l-4 border-blue-400">
                      <p className="font-semibold text-gray-900">Banka A:</p>
                      <p className="text-gray-700 text-sm">Mesačná splátka: <strong>180 €</strong></p>
                      <p className="text-gray-700 text-sm">Počet mesiacov: <strong>72 (6 rokov)</strong></p>
                      <p className="text-gray-700 text-sm">Celková splátka: <strong>12 960 €</strong></p>
                    </div>
                    <div className="bg-white p-4 rounded border-l-4 border-purple-400">
                      <p className="font-semibold text-gray-900">Banka B:</p>
                      <p className="text-gray-700 text-sm">Mesačná splátka: <strong>240 €</strong></p>
                      <p className="text-gray-700 text-sm">Počet mesiacov: <strong>48 (4 roky)</strong></p>
                      <p className="text-gray-700 text-sm">Celková splátka: <strong>11 520 €</strong></p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Na čo by mali rodičia myslieť pri rozhodovaní?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(8, 'A')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Vybrať Banku A, lebo nižšia mesačná splátka je vždy lepšia</div>
                  <div className="text-sm text-gray-600">Nízka splátka = najlepšia voľba</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'B')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Vybrať Banku B, lebo celková splátka je nižšia, aj keď to bude ťažké zvládnuť</div>
                  <div className="text-sm text-gray-600">Lacnejšia celková suma = správna voľba</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'C')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-3 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Zistiť, koľko si rodičia môžu reálne dovoliť mesačne, a potom sa rozhodnúť</div>
                  <div className="text-sm text-gray-600">Najprv zvážiť možnosti rodiny</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'D')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Nekúpiť auto vôbec, lebo pôžička je vždy zlá</div>
                  <div className="text-sm text-gray-600">Vyhnúť sa pôžičke za každú cenu</div>
                </button>
              </div>

              {selectedAnswers[8] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[8] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[8] === 'C' ? '✅ Výborne!' : '⚠️ Zamysli sa...'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[8] === 'A' ? 'Nízka mesačná splátka sa zdá príjemná, ale celková suma je vyššia. Musíš zvážiť, či je rodina schopná zvládnuť vyššiu splátku, aby ušetrila celkovo.' :
                      selectedAnswers[8] === 'B' ? 'Nižšia celková suma znie dobre, ale ak rodina nevie bezpečne zvládnuť vyššiu mesačnú splátku, môže to viesť k finančným problémom.' :
                      selectedAnswers[8] === 'C' ? 'Presne tak! Najprv treba zistiť, koľko si rodina môže mesačne dovoliť. Potom vybrať banku, ktorá ponúka najlepšiu rovnováhu medzi dostupnosťou a celkovými nákladmi.' :
                      'Auto je v tomto prípade skutočná potreba (staré nefunguje). Pôžička má zmysel, ak je dobre zvážená. Nejde o to vyhnúť sa pôžičke úplne, ale rozhodnúť sa múdro.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[8] === 'A' ? 'Nižšia mesačná splátka vyzerá príjemne, ale celková splátka je o 1 440 € vyššia. To je veľký rozdiel. Treba zvážiť, či rodina dokáže mesačne zvládnuť o 60 € viac, aby ušetrila celkovo.' :
                      selectedAnswers[8] === 'B' ? 'Banka B je celkovo lacnejšia, ale ak rodina nedokáže bezpečne platiť 240 € mesačne, môže to viesť k problémom. Najlepšie rozhodnutie závisí od toho, čo si rodina môže dovoliť.' :
                      selectedAnswers[8] === 'C' ? 'Správne! Rozhodnutie závisí od skutočnej situácie rodiny. Ak si môžu dovoliť 240 € mesačne, Banka B je lepšia (ušetria 1 440 €). Ak nie, Banka A je bezpečnejšia. Múdre rozhodnutie vyvažuje celkové náklady a mesačnú dostupnosť.' :
                      'Auto je v tomto prípade skutočná potreba. Pôžička nie je automaticky zlá – záleží na okolnostiach. Kľúč je vybrať ponuku, ktorá je bezpečná a zároveň čo najlacnejšia.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SLIDE 9: Monthly affordability vs total cost */}
          {currentStep === 9 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">💰</div>
                  <h2 className="text-2xl font-bold text-gray-900">Čo si rodina dokáže dovoliť?</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Rodičia si spočítali, koľko im mesačne zostáva po všetkých nevyhnutných výdavkoch (jedlo, bývanie, energie): <strong>300 €</strong>.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Otec hovorí: <em>"Banka A má splátku 180 €. To je prijateľné, zostane nám ešte 120 € na život."</em>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Mama hovorí: <em>"Ale Banka B má splátku 240 €. Bude to ťažšie, ale ušetríme 1 440 €. Čo ak to skúsime?"</em>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Aké je najlepšie rozhodnutie?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(9, 'A')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Určite Banka B, lebo ušetria najviac peňazí</div>
                  <div className="text-sm text-gray-600">Nižšia celková suma = najlepšia voľba</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'B')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-3 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Záleží na tom, čo si rodina môže bezpečne dovoliť. Splátka 240 € by im nechala len 60 € mesačne, čo je veľmi rizikové</div>
                  <div className="text-sm text-gray-600">Treba porovnať nielen celkovú cenu, ale aj to, čo rodina zvládne mesačne platiť</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'C')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Určite Banka A, lebo nižšia splátka je vždy lepšia</div>
                  <div className="text-sm text-gray-600">Nízka splátka = správna voľba</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'D')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Kúpiť lacnejšie auto, aby nepotrebovali úver</div>
                  <div className="text-sm text-gray-600">Vyhnúť sa úveru úplne</div>
                </button>
              </div>

              {selectedAnswers[9] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[9] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[9] === 'B' ? '✅ Správne!' : '💭 Premysli si to...'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[9] === 'A' ? 'Ušetriť 1 440 € znie dobre, ale ak rodine zostane len 60 € mesačne, každý nečakaný výdavok (lekár, oprava) môže byť problém. Bezpečnosť je dôležitá.' :
                      selectedAnswers[9] === 'B' ? 'Presne tak! 240 € by nechalo rodine len 60 € mesačne. To je veľmi málo – každý nečakaný výdavok by bol problém. Banka A je bezpečnejšia, aj keď celkovo drahšia.' :
                      selectedAnswers[9] === 'C' ? 'Nižšia splátka je príjemná, ale nie vždy najlepšia. Musíš zvážiť aj celkovú sumu. Niekedy sa oplatí platiť viac mesačne, ak to vedieš zvládnuť.' :
                      'Lacnejšie auto môže byť riešenie, ale v tomto prípade rodičia potrebujú spoľahlivé auto. Nejde o to vyhnúť sa úveru úplne, ale vybrať múdru ponuku.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[9] === 'A' ? 'Banka B ušetrí 1 440 €, ale splátka 240 € by nechala rodine len 60 € mesačne. To je veľmi málo – každý nečakaný výdavok (lekár, oprava) by spôsobil problém. Banka A je bezpečnejšia (zostane 120 € mesačne), aj keď celkovo drahšia.' :
                      selectedAnswers[9] === 'B' ? 'Múdre rozhodnutie vyvažuje celkovú cenu a mesačnú bezpečnosť. Banka B je lacnejšia celkovo, ale 240 € by nechalo len 60 € mesačne – to je riskanté. Banka A je bezpečnejšia (zostane 120 € mesačne), aj keď celkovo drahšia o 1 440 €.' :
                      selectedAnswers[9] === 'C' ? 'Nižšia splátka sa zdá príjemná, ale celková suma je o 1 440 € vyššia. To je veľký rozdiel. Najlepšie rozhodnutie závisí od toho, čo si rodina môže bezpečne dovoliť.' :
                      'Lacnejšie auto môže byť riešenie, ale rodičia potrebujú spoľahlivé auto (staré nefunguje). Úver má v tomto prípade zmysel, ak je dobre zvážený.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SLIDE 10: Long-term consequences */}
          {currentStep === 10 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">📅</div>
                  <h2 className="text-2xl font-bold text-gray-900">Dlhodobé dôsledky</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Rodičia sa nakoniec rozhodli pre <strong>Banku A</strong> (180 € mesačne, celkovo 12 960 €), pretože 240 € mesačne by bolo príliš riskantné.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    O pol roka sa ukázalo, že to bolo múdre rozhodnutie. Babka sa musela liečiť (300 € náklady) a rodina to zvládla, lebo im mesačne zostávalo 120 € rezervy.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Otec hovorí: <em>"Keby sme brali Banku B s platbou 240 €, mali by sme vážny problém. Takto to zvládneme."</em>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo sa z tohto naučíš?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(10, 'A')
                    setCurrentStep(11)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Vždy vybrať najlacnejšiu celkovú sumu, nech to stojí čo to stojí</div>
                  <div className="text-sm text-gray-600">Lacnejšia celková cena = najlepšia voľba</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'B')
                    setCurrentStep(11)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Vždy vybrať najnižšiu mesačnú splátku, aj keď celkovo zaplatíš viac</div>
                  <div className="text-sm text-gray-600">Nízka splátka = najlepšia voľba</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'C')
                    setCurrentStep(11)
                  }}
                  className="w-full text-left p-3 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Najlepšie rozhodnutie vyvažuje celkové náklady a mesačnú bezpečnosť</div>
                  <div className="text-sm text-gray-600">Vyvážiť cenu a dostupnosť</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'D')
                    setCurrentStep(11)
                  }}
                  className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Nečakané výdavky sa nestávajú, netreba sa tým trápiť</div>
                  <div className="text-sm text-gray-600">To sa stane málokedy</div>
                </button>
              </div>

              {selectedAnswers[10] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[10] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[10] === 'C' ? '✅ Výborne!' : '💭 Zamysli sa...'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[10] === 'A' ? 'Najlacnejšia celková suma nie je vždy najlepšia, ak mesačná splátka je príliš vysoká. Musíš zvážiť, čo si môžeš bezpečne dovoliť.' :
                      selectedAnswers[10] === 'B' ? 'Najnižšia splátka sa zdá príjemná, ale celková suma môže byť oveľa vyššia. Musíš zvážiť obe strany – mesačnú dostupnosť aj celkové náklady.' :
                      selectedAnswers[10] === 'C' ? 'Presne tak! Múdre rozhodnutie vyvažuje celkové náklady a mesačnú bezpečnosť. Niekedy je lepšie zaplatiť trochu viac celkovo, ak to znamená bezpečnejšiu mesačnú splátku.' :
                      'Nečakané výdavky prídu častejšie, ako si myslíš (lekár, oprava, darček). Preto je dôležité mať mesačnú rezervu a nevyužiť celú dostupnú sumu na splátky.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[10] === 'A' ? 'Banka B bola celkovo lacnejšia (1 440 € úspora), ale splátka 240 € by nechala rodine len 60 € mesačne. Keď prišiel nečakaný výdavok (300 €), mali by vážny problém. Banka A je celkovo drahšia, ale bezpečnejšia.' :
                      selectedAnswers[10] === 'B' ? 'Najnižšia splátka sa zdá príjemná, ale celková suma môže byť oveľa vyššia. V tomto prípade Banka A stála o 1 440 € viac. Najlepšie rozhodnutie vyvažuje obe strany.' :
                      selectedAnswers[10] === 'C' ? 'Správne! Rodičia vybrali Banku A, aj keď celkovo stála o 1 440 € viac. Splátka 180 € nechala rezervu 120 € mesačne, čo sa ukázalo ako múdre pri nečakanom výdavku. Múdre rozhodnutie vyvažuje celkové náklady a mesačnú bezpečnosť.' :
                      'Nečakané výdavky prídu častejšie, ako si myslíš (lekár, oprava, darček). V tomto prípade prišiel náklad 300 € na liečbu. Keby rodina mala splátku 240 € (len 60 € rezerva), bol by to vážny problém. Preto je dôležité mať mesačnú rezervu.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* SLIDE 11: Overall scenario evaluation and reflection */}
          {currentStep === 11 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              {/* Quality-based final reflection */}
              <div className="p-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-2xl">📝</span>
                  Tvoje rozhodovanie
                </h3>
                
                {(() => {
                  const score = getDecisionQuality()
                  
                  if (score >= 6) {
                    return (
                      <div className="space-y-3">
                        <p className="text-green-700 font-semibold">
                          ✅ Výborne! Rozhodoval si veľmi múdro.
                        </p>
                        <p className="text-gray-700">
                          Pochopil si, že <strong>pôžičky nie sú zadarmo</strong> – platíš úroky navyše. Uvedomil si, že <strong>nízka mesačná splátka neznamená dobrú ponuku</strong>, lebo celková suma môže byť vyššia.
                        </p>
                        <p className="text-gray-700">
                          Dokázal si <strong>rozlíšiť potrebu od želania</strong>. A pochopil si, že <strong>splátky obmedzujú flexibilitu</strong> – keď príde nečakaný výdavok, môže to byť problém.
                        </p>
                        <p className="text-gray-700 font-medium">
                          💡 <strong>Záver:</strong> Pôžička má zmysel len pri skutočnej potrebe. Inak je lepšie počkať a ušetriť – zaplatíš menej, nebudeš mať záväzky a budeš flexibilnejší.
                        </p>
                      </div>
                    )
                  } else if (score >= 4) {
                    return (
                      <div className="space-y-3">
                        <p className="text-yellow-700 font-semibold">
                          ⚠️ Niektoré rozhodnutia boli dobré, iné menej.
                        </p>
                        <p className="text-gray-700">
                          Niekedy si si uvedomil, že <strong>pôžičky majú úroky</strong> a že <strong>nízka mesačná splátka môže byť pasca</strong>. To je dobré!
                        </p>
                        <p className="text-gray-700">
                          Ale niekoľkokrát si <strong>podcenil budúce dôsledky</strong> – splátky ťa zaviažu na niekoľko mesiacov a obmedzia flexibilitu. A možno si <strong>nezohľadnil rozdiel medzi potrebou a želaním</strong>.
                        </p>
                        <p className="text-gray-700 font-medium">
                          💡 <strong>Čo si zapamätať:</strong> Pôžička má zmysel len pri skutočnej potrebe. Inak je lepšie počkať a ušetriť – zaplatíš menej a budeš flexibilnejší.
                        </p>
                      </div>
                    )
                  } else {
                    return (
                      <div className="space-y-3">
                        <p className="text-red-700 font-semibold">
                          ⚠️ Tvoje rozhodovanie bolo príliš zamerané na súčasný moment.
                        </p>
                        <p className="text-gray-700">
                          Niekoľkokrát si <strong>podcenil celkové náklady pôžičky</strong> – úroky znamenajú, že zaplatíš viac ako cena tovaru. A <strong>nízka mesačná splátka ťa oklamala</strong> – celková suma môže byť oveľa vyššia.
                        </p>
                        <p className="text-gray-700">
                          Tiež si <strong>nezvážil budúce dôsledky</strong> – splátky ťa zaviažu na niekoľko mesiacov a keď príde nečakaný výdavok, môže to byť vážny problém. A možno si <strong>nezohľadnil rozdiel medzi potrebou a želaním</strong>.
                        </p>
                        <p className="text-gray-700 font-medium">
                          💡 <strong>Čo by si mal vedieť:</strong> Pôžička nie je riešenie na každé želanie. Úroky a splátky znamenajú, že zaplatíš viac a budeš menej flexibilný. Počkať a ušetriť je často múdrejšie.
                        </p>
                        <p className="text-gray-700">
                          <strong>Zapamätaj si:</strong> Pred pôžičkou sa vždy opýtaj: Je to skutočná potreba? Koľko celkovo zaplatím? Môžem počkať a ušetriť?
                        </p>
                      </div>
                    )
                  }
                })()}
              </div>

              <div className="mt-8 flex justify-center gap-4">
                <button
                  onClick={() => navigate('/scenarios/8')}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition"
                >
                  Späť na scenáre
                </button>
                <button
                  onClick={() => {
                    setCurrentStep(1)
                    setSelectedAnswers({})
                  }}
                  className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition"
                >
                  Skúsiť znova
                </button>
              </div>
            </div>
          )}


        </div>
      </div>

    </div>
  )
}

export default BorrowOrWaitScenario
